/**
 * Reset Password Page - ViTex
 */

import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { KeyRound } from 'lucide-react';
import { authAPI } from '../services/api';
import { useI18n } from '../i18n/I18nContext';
import Footer from '../components/Footer';
import Logo from '../components/Logo';
import '../styles/Auth.css';

const ResetPassword = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [formData, setFormData] = useState({ password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setError(t('auth.reset.tokenMissing'));
      return;
    }
    if (formData.password !== formData.confirm) {
      setError(t('auth.reset.passwordsMustMatch'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await authAPI.resetPassword(token, formData.password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.message || t('auth.reset.couldNotReset'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-shell">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-logo">
            <Logo height={40} />
          </div>

          <h1>{t('auth.reset.title')}</h1>
          <p className="auth-subtitle">{t('auth.reset.subtitle')}</p>

          {error && <div className="error-message">{error}</div>}

          {success ? (
              <div className="success-message" style={{ marginTop: 16 }}>
                {t('auth.reset.success')}
              </div>
          ) : (
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="password">{t('auth.reset.newPassword')}</label>
                  <input
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      minLength={8}
                      placeholder={t('auth.reset.newPasswordPlaceholder')}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirm">{t('auth.reset.confirmPassword')}</label>
                  <input
                      type="password"
                      id="confirm"
                      name="confirm"
                      value={formData.confirm}
                      onChange={handleChange}
                      required
                      minLength={8}
                      placeholder={t('auth.reset.confirmPasswordPlaceholder')}
                  />
                </div>

                <button
                    type="submit"
                    className="btn-primary-auth"
                    disabled={loading || !token}
                >
                  {loading ? t('auth.reset.updating') : (
                      <>
                        <KeyRound size={20} /> {t('auth.reset.updatePassword')}
                      </>
                  )}
                </button>
              </form>
          )}

          <p className="auth-footer">
            <Link to="/login">{t('auth.forgot.backToSignIn')}</Link>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ResetPassword;
